import {useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {FieldError} from '@/components/field-error'
import {HelpIcon} from '@/components/help-icon'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {
  useDeclararNormaPropia,
  useEditarNormaPropia,
  type LegalItem,
  type NormaPropiaPayload,
} from '@/hooks/sgsst/matriz_legal/use-matriz-legal'
import {isNormaEnCatalogo, translateApiError} from '@/lib/errors'
import {validateAnio, validateRequired, validateUrl} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

const TIPOS = ['ley', 'decreto', 'decreto_unico', 'resolucion', 'circular', 'acuerdo'] as const
const EMISORES = ['congreso', 'presidencia', 'mintrabajo', 'minsalud', 'dian', 'otro'] as const

type Campos = {
  tipo: string
  numero: string
  anio: string
  emisor: string
  titulo: string
  tema: string
  fuenteUrl: string
  nota: string
}

const VACIO: Campos = {
  tipo: 'acuerdo',
  numero: '',
  // Solo prellena el campo: el rango lo decide el backend con `bogota_today`.
  // nosemgrep: browser-clock-business-date
  anio: String(new Date().getFullYear()),
  emisor: 'otro',
  titulo: '',
  tema: '',
  fuenteUrl: '',
  nota: '',
}

function desdeItem(item: LegalItem): Campos {
  return {
    tipo: item.tipo,
    numero: item.numero,
    anio: String(item.anio),
    emisor: item.emisor,
    titulo: item.titulo,
    tema: item.articulos_clave ?? '',
    fuenteUrl: item.fuente_url ?? '',
    nota: item.nota_aplicabilidad ?? '',
  }
}

function validar(campos: Campos): Partial<Record<keyof Campos, string>> {
  return {
    numero: validateRequired(campos.numero) ?? undefined,
    anio: validateAnio(campos.anio) ?? undefined,
    titulo: validateRequired(campos.titulo) ?? undefined,
    nota: validateRequired(campos.nota) ?? undefined,
    fuenteUrl: validateUrl(campos.fuenteUrl) ?? undefined,
  }
}

function payloadDe(campos: Campos): NormaPropiaPayload {
  return {
    tipo: campos.tipo as NormaPropiaPayload['tipo'],
    numero: campos.numero.trim(),
    anio: Number.parseInt(campos.anio.trim(), 10),
    emisor: campos.emisor as NormaPropiaPayload['emisor'],
    titulo: campos.titulo.trim(),
    tema: campos.tema.trim() || null,
    fuente_url: campos.fuenteUrl.trim() || null,
    nota_aplicabilidad: campos.nota.trim(),
  }
}

/** La nota de aplicabilidad es obligatoria: es la trazabilidad que una norma derivada trae en su cita. */
export function NormaPropiaDialog({
  businessId,
  item,
  onClose,
  onIrANovedades,
}: {
  businessId: string
  item: LegalItem | null
  onClose: () => void
  onIrANovedades: () => void
}) {
  const {t} = useTranslation()
  const declarar = useDeclararNormaPropia(businessId)
  const editar = useEditarNormaPropia(businessId)
  const [campos, setCampos] = useState<Campos>(item ? desdeItem(item) : VACIO)
  const [errores, setErrores] = useState<Partial<Record<keyof Campos, string>>>({})
  const [error, setError] = useState<string | null>(null)
  const [enCatalogo, setEnCatalogo] = useState(false)
  const [nodo, setNodo] = useState<HTMLElement | null>(null)

  const editando = item !== null
  const pendiente = declarar.isPending || editar.isPending

  function set(campo: keyof Campos, valor: string) {
    setCampos((prev) => ({...prev, [campo]: valor}))
    setErrores((prev) => ({...prev, [campo]: undefined}))
    setError(null)
    setEnCatalogo(false)
  }

  function blur(campo: keyof Campos) {
    setErrores((prev) => ({...prev, [campo]: validar(campos)[campo]}))
  }

  function onError(err: unknown) {
    setEnCatalogo(isNormaEnCatalogo(err))
    setError(translateApiError(err))
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const hallazgos = validar(campos)
    setErrores(hallazgos)
    if (Object.values(hallazgos).some(Boolean)) return
    const payload = payloadDe(campos)
    if (item?.norma_propia_id) {
      editar.mutate({...payload, propiaId: item.norma_propia_id}, {onSuccess: onClose, onError})
      return
    }
    declarar.mutate(payload, {onSuccess: onClose, onError})
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      {/* Ref de callback: Radix monta el contenido tras el primer efecto. Sin `contenedor`
          los popovers salen del focus scope y el primer Escape cierra el formulario. */}
      <DialogContent ref={setNodo} className="max-h-[85vh] overflow-y-auto" data-testid="norma-propia-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5">
            {t(editando ? 'matrizLegal.propia.editTitle' : 'matrizLegal.propia.title')}
            <HelpIcon
              label={t('common.help.iconLabelFor', {campo: t('matrizLegal.propia.title')})}
              testId="norma-propia-tip"
              contenedor={nodo}>
              {t('matrizLegal.propia.tip')}
            </HelpIcon>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="norma-propia-tipo">{t('matrizLegal.propia.tipo')}</Label>
            <select
              id="norma-propia-tipo"
              data-testid="norma-propia-tipo"
              value={campos.tipo}
              onChange={(e) => set('tipo', e.target.value)}
              className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm">
              {TIPOS.map((tipo) => (
                <option key={tipo} value={tipo}>
                  {t(`matrizLegal.propia.tipos.${tipo}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="norma-propia-numero">{t('matrizLegal.propia.numero')}</Label>
            <Input
              id="norma-propia-numero"
              data-testid="norma-propia-numero"
              value={campos.numero}
              onChange={(e) => set('numero', e.target.value)}
              onBlur={() => blur('numero')}
              maxLength={40}
              aria-invalid={!!errores.numero}
            />
            <FieldError error={errores.numero} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="norma-propia-anio">{t('matrizLegal.propia.anio')}</Label>
            <Input
              id="norma-propia-anio"
              data-testid="norma-propia-anio"
              inputMode="numeric"
              value={campos.anio}
              onChange={(e) => set('anio', e.target.value)}
              onBlur={() => blur('anio')}
              maxLength={4}
              aria-invalid={!!errores.anio}
            />
            <FieldError error={errores.anio} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="norma-propia-emisor">{t('matrizLegal.propia.emisor')}</Label>
            <select
              id="norma-propia-emisor"
              data-testid="norma-propia-emisor"
              value={campos.emisor}
              onChange={(e) => set('emisor', e.target.value)}
              className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm">
              {EMISORES.map((emisor) => (
                <option key={emisor} value={emisor}>
                  {t(`matrizLegal.propia.emisores.${emisor}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="norma-propia-titulo">{t('matrizLegal.propia.titulo')}</Label>
            <Input
              id="norma-propia-titulo"
              data-testid="norma-propia-titulo"
              value={campos.titulo}
              onChange={(e) => set('titulo', e.target.value)}
              onBlur={() => blur('titulo')}
              maxLength={300}
              aria-invalid={!!errores.titulo}
            />
            <FieldError error={errores.titulo} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="norma-propia-tema">{t('matrizLegal.propia.tema')}</Label>
            <Input
              id="norma-propia-tema"
              data-testid="norma-propia-tema"
              value={campos.tema}
              onChange={(e) => set('tema', e.target.value)}
              maxLength={tope('NOTA')}
              placeholder={t('matrizLegal.propia.temaPlaceholder')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="norma-propia-fuente">{t('matrizLegal.propia.fuente')}</Label>
            <Input
              id="norma-propia-fuente"
              data-testid="norma-propia-fuente"
              value={campos.fuenteUrl}
              onChange={(e) => set('fuenteUrl', e.target.value)}
              onBlur={() => blur('fuenteUrl')}
              maxLength={tope('NOTA')}
              placeholder="https://"
              aria-invalid={!!errores.fuenteUrl}
            />
            <FieldError error={errores.fuenteUrl} />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="norma-propia-nota">{t('matrizLegal.propia.nota')}</Label>
              <HelpIcon
                label={t('common.help.iconLabelFor', {campo: t('matrizLegal.propia.nota')})}
                testId="norma-propia-nota-tip"
                contenedor={nodo}>
                {t('matrizLegal.propia.notaTip')}
              </HelpIcon>
            </div>
            <Textarea
              id="norma-propia-nota"
              data-testid="norma-propia-nota"
              value={campos.nota}
              onChange={(e) => set('nota', e.target.value)}
              onBlur={() => blur('nota')}
              maxLength={1000}
              rows={3}
              placeholder={t('matrizLegal.propia.notaPlaceholder')}
              aria-invalid={!!errores.nota}
            />
            <FieldError error={errores.nota} />
          </div>

          {error ? (
            <div className="space-y-1" data-testid="norma-propia-error">
              <p className="text-sm text-destructive">{error}</p>
              {enCatalogo ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="norma-propia-ir-novedades"
                  onClick={() => {
                    onClose()
                    onIrANovedades()
                  }}>
                  {t('matrizLegal.propia.irANovedades')}
                </Button>
              ) : null}
            </div>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={pendiente} data-testid="norma-propia-submit">
              {t(editando ? 'matrizLegal.propia.saveEdit' : 'matrizLegal.propia.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
